import useTheme from "../../hooks/useTheme";
import { arClass } from "../../utils/useArClass";
import Card from "./Card";
import { motion } from "framer-motion";

const ShimmerEffect = () => (
  <motion.div
    className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent max-sm:via-white/10"
    animate={{
      x: ["-100%", "100%"],
    }}
    transition={{
      duration: 2,
      repeat: Infinity,
      repeatType: "loop",
      ease: "linear",
    }}
  />
);

// Enhanced Stat Card with Animation
const StatCard = ({
  icon: Icon,
  label,
  value,
  trend,
  className = "",
  color = "indigo",
  compact = false,
}) => {
  const theme = useTheme();

  const colorClasses = {
    indigo: theme.darkMode
      ? "from-indigo-500/20 to-purple-500/20 border-indigo-500/30"
      : "from-indigo-100/80 to-purple-100/80 border-indigo-200/50",
    emerald: theme.darkMode
      ? "from-emerald-500/20 to-teal-500/20 border-emerald-500/30"
      : "from-emerald-100/80 to-teal-100/80 border-emerald-200/50",
    amber: theme.darkMode
      ? "from-amber-500/20 to-orange-500/20 border-amber-500/30"
      : "from-amber-100/80 to-orange-100/80 border-amber-200/50",
    rose: theme.darkMode
      ? "from-rose-500/20 to-pink-500/20 border-rose-500/30"
      : "from-rose-100/80 to-pink-100/80 border-rose-200/50",
  };

  const iconColors = {
    indigo: theme.darkMode ? "text-indigo-400" : "text-indigo-600",
    emerald: theme.darkMode ? "text-emerald-400" : "text-emerald-600",
    amber: theme.darkMode ? "text-amber-400" : "text-amber-600",
    rose: theme.darkMode ? "text-rose-400" : "text-rose-600",
  };

  return (
    <Card
      className={`p-6 overflow-hidden ${className} ${colorClasses[color]} ${
        compact ? "max-sm:p-4" : ""
      }`}
      hover
      gradient
    >
      <div className={arClass("absolute inset-0 bg-gradient-to-br opacity-50")} />
      <div className={arClass("relative z-10 flex items-start justify-between")}>
        <div className={arClass("flex-1 min-w-0")}>
          <div className={arClass("flex items-center gap-2 mb-2")}>
            <p
              className={arClass(`text-sm font-medium truncate ${
                compact ? "max-sm:text-xs" : ""
              } ${theme.colors.text.muted}`)}
            >
              {label}
            </p>
            {trend && (
              <span
                className={arClass(`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ${
                  compact ? "max-sm:text-xss max-sm:px-1.5 max-sm:py-0.5" : ""
                } ${
                  trend.startsWith("+")
                    ? theme.darkMode
                      ? "bg-emerald-500/20 text-emerald-400"
                      : "bg-emerald-200 text-emerald-700"
                    : trend.startsWith("-")
                    ? theme.darkMode
                      ? "bg-rose-500/20 text-rose-400"
                      : "bg-rose-200 text-rose-700"
                    : theme.darkMode
                    ? "bg-gray-500/20 text-gray-400"
                    : "bg-gray-200 text-gray-700"
                }`)}
              >
                {trend}
              </span>
            )}
          </div>
          <p
            className={arClass(`font-bold truncate ${
              compact
                ? "text-2xl max-sm:text-lg"
                : "text-3xl max-sm:text-2xl"
            } ${
              theme.darkMode
                ? "bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent"
                : theme.colors.text.primary
            }`)}
          >
            {value}
          </p>
        </div>
        <motion.div
          className={arClass(`rounded-xl bg-gradient-to-br ${
            colorClasses[color]
          } ${compact ? "p-3 max-sm:p-2" : "p-4 max-sm:p-3"}`)}
          whileHover={{ scale: 1.1, rotate: 5 }}
          transition={{ type: "spring", stiffness: 300 }}
        >
          <Icon
            className={`${compact ? "w-6 h-6 max-sm:w-5 max-sm:h-5" : "w-7 h-7 max-sm:w-6 max-sm:h-6"} ${iconColors[color]}`}
          />
        </motion.div>
      </div>
    </Card>
  );
};

const StatCardSkeleton = ({ compact = false }) => {
  const theme = useTheme();

  return (
    <Card className={`p-6 ${compact ? "max-sm:p-4" : ""}`} gradient>
      <ShimmerEffect />
      <div className="flex items-start justify-between relative z-10">
        <div className="space-y-3 flex-1 min-w-0">
          <div
            className={`h-4 rounded-lg ${
              compact ? "w-20 max-sm:w-16" : "w-24 max-sm:w-20"
            } ${
              theme.darkMode ? "bg-gray-800" : "bg-gray-200"
            } animate-pulse`}
          />
          <div
            className={`rounded-lg ${
              compact
                ? "h-6 w-16 max-sm:h-5 max-sm:w-14"
                : "h-8 w-20 max-sm:h-6 max-sm:w-16"
            } ${
              theme.darkMode ? "bg-gray-800" : "bg-gray-200"
            } animate-pulse`}
          />
        </div>
        <div
          className={`rounded-xl ${
            compact
              ? "w-12 h-12 max-sm:w-10 max-sm:h-10"
              : "w-14 h-14 max-sm:w-12 max-sm:h-12"
          } ${
            theme.darkMode ? "bg-gray-800" : "bg-gray-200"
          } animate-pulse`}
        />
      </div>
    </Card>
  );
};

export default StatCard;
export { StatCardSkeleton };